import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Form, InputGroup, Nav, Spinner } from 'react-bootstrap';
import { ClientSelectGroup } from '@/components/ClientSelectGroup';
import { DeployBlockNotice } from '@/components/clientFlags/ClientTags';
import { useAuth } from '@/contexts/AuthContext';
import { useClientFlags } from '@/hooks/useClientFlags';
import { getConfigValue } from '@/services/configService';
import {
  BlockedClientsChangedError,
  DeployBlockedError,
  getDeploymentLock,
  getGroupConcurrencyBounds,
  listDeploymentGroups,
  startDeployment,
  startGroupDeployment,
  waitForDeploymentRecord,
} from '@/services/deploymentService';
import type { RepositoryName } from '@/services/imageTagService';
import type { Client, DeploymentGroup, ECRImage } from '@/types';
import { describeSkips, skipKind, skipLine, splitGroupClients } from '@/utils/groupRunSkips';
import type { ImagesByChannel } from '@/utils/deployStatus';
import { formatNzDateTime } from '@/utils/relativeTime';
import { ConfirmDialog } from './ConfirmDialog';
import { GroupEditorModal } from './GroupEditorModal';
import { SkippedClientsNotice } from './SkippedClientsNotice';
import './deployments.css';

type Channel = RepositoryName;

const CHANNELS: { id: Channel; label: string }[] = [
  { id: 'numa-deploy', label: 'Production' },
  { id: 'numa-deploy-dev', label: 'Dev' },
];

/** What a link into the page asks the form to start with. */
export interface StartPreset {
  channel?: Channel;
  image?: string;
}

export type StartedRun =
  | { kind: 'deploy' | 'plan'; deploymentId: string; clientName: string }
  | { kind: 'group'; groupRunId: string };

interface StartCardProps {
  clients: Client[];
  imagesByChannel: ImagesByChannel;
  /** Clients and images are still loading. */
  loading: boolean;
  preset?: StartPreset | null;
  /** Clients with a deploy running now, from the list on the page. */
  liveClients: Set<string>;
  onStarted: (run: StartedRun) => void;
}

function imageOptionLabel(image: ECRImage, newest: boolean): string {
  const parts = [image.customName ? `${image.customName} (${image.tag})` : image.tag, formatNzDateTime(image.pushedAt)];
  // A tag is usually the commit it was built from. Saying it twice helps nobody.
  if (image.gitCommit && image.gitCommit !== 'unknown' && image.gitCommit !== image.tag) parts.push(image.gitCommit);
  if (image.gitBranch && image.gitBranch !== 'feature') parts.push(image.gitBranch);
  return `${parts.join(' · ')}${newest ? ' (newest)' : ''}`;
}

/**
 * The start form, always open at the top of the page: the channel and the mode
 * in the header row, who to deploy to on the left, the image, the label and the
 * buttons on the right.
 */
export function StartCard({ clients, imagesByChannel, loading, preset, liveClients, onStarted }: StartCardProps) {
  const { user } = useAuth();
  const sfnArn = getConfigValue('DEPLOYMENT_SFN_ARN');
  const groupSfnArn = getConfigValue('DEPLOYMENT_GROUP_SFN_ARN');
  const groupsTable = getConfigValue('DEPLOYMENT_GROUPS_TABLE');
  const groupConfigMissing = !groupSfnArn || !groupsTable;

  const bounds = useMemo(() => getGroupConcurrencyBounds(), []);
  const defaultConcurrency = bounds.default ?? 10;
  // The real limit comes from config. 200 is the pipeline's own ceiling, used when config is absent on a laptop.
  const maxConcurrency = bounds.max ?? 200;
  const clamp = useCallback((value: number) => Math.max(1, Math.min(value, maxConcurrency)), [maxConcurrency]);

  const [channel, setChannel] = useState<Channel>('numa-deploy');
  const [mode, setMode] = useState<'single' | 'group'>('single');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [label, setLabel] = useState('');
  const [groupLabel, setGroupLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Why a start was refused while its confirm step is open. Shown inside that step, which stays open.
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<'deploy' | 'plan' | 'group' | null>(null);
  const [confirming, setConfirming] = useState<'single' | 'group' | null>(null);
  const [lockHeld, setLockHeld] = useState(false);
  // Which clients are marked do not deploy, and which are inactive.
  const flags = useClientFlags();

  const [groups, setGroups] = useState<DeploymentGroup[]>([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [concurrency, setConcurrency] = useState(defaultConcurrency);
  const [editor, setEditor] = useState<{ open: boolean; group: DeploymentGroup | null }>({ open: false, group: null });

  const card = useRef<HTMLDivElement>(null);
  const images = imagesByChannel[channel];
  const sortedImages = useMemo(
    () => [...images].sort((a, b) => new Date(b.pushedAt).getTime() - new Date(a.pushedAt).getTime()),
    [images]
  );
  const selectedImage = sortedImages.find((img) => img.tag === selectedTag);

  // A link into the page can name the channel and the image to start with. The image is held
  // here until its channel's list is on screen, then picked.
  const wantedTag = useRef<string | null>(null);
  useEffect(() => {
    if (!preset) return;
    if (preset.channel) setChannel(preset.channel);
    if (preset.image) wantedTag.current = preset.image;
    card.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [preset]);

  // Keep the selected image inside the channel: the one a link asked for, else the newest
  // when the current pick is not in the list.
  useEffect(() => {
    if (sortedImages.length === 0) return;
    const wanted = wantedTag.current;
    if (wanted && sortedImages.some((img) => img.tag === wanted)) {
      wantedTag.current = null;
      setSelectedTag(wanted);
      return;
    }
    if (!sortedImages.some((img) => img.tag === selectedTag)) setSelectedTag(sortedImages[0].tag);
  }, [sortedImages, selectedTag]);

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      setGroups(await listDeploymentGroups());
      setGroupsLoaded(true);
    } catch (e) {
      console.error('Failed to load deployment groups:', e);
      setError(e instanceof Error ? e.message : 'Could not load the deployment groups.');
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  // Groups are only needed once somebody picks group mode.
  useEffect(() => {
    if (mode === 'group' && !groupsLoaded && !loadingGroups && !groupConfigMissing) void loadGroups();
  }, [mode, groupsLoaded, loadingGroups, groupConfigMissing, loadGroups]);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.groupName === selectedGroupName) ?? null,
    [groups, selectedGroupName]
  );

  useEffect(() => {
    if (groups.length === 0) {
      if (selectedGroupName) setSelectedGroupName('');
      return;
    }
    if (!selectedGroupName || !groups.some((g) => g.groupName === selectedGroupName)) {
      setSelectedGroupName(groups[0].groupName);
    }
  }, [groups, selectedGroupName]);

  // Each group can carry its own number of clients to deploy at once.
  useEffect(() => {
    if (selectedGroup) setConcurrency(clamp(selectedGroup.maxConcurrency ?? defaultConcurrency));
  }, [selectedGroup, defaultConcurrency, clamp]);

  // One key read tells whether the client is locked by a deploy the page's list does not show.
  useEffect(() => {
    setLockHeld(false);
    if (!selectedClient) return;
    let cancelled = false;
    getDeploymentLock(selectedClient)
      .then((lock) => {
        if (!cancelled) setLockHeld(Boolean(lock));
      })
      .catch((e) => console.warn('Could not check the deploy lock', e));
    return () => {
      cancelled = true;
    };
  }, [selectedClient]);

  const clientBusy = Boolean(selectedClient) && (liveClients.has(selectedClient) || lockHeld);
  const selectedClientObj = clients.find((c) => c.name === selectedClient);
  // The selected client is marked do not deploy. The card warns, and a deploy needs the client's name typed.
  const selectedBlock = selectedClient ? flags.deployBlock(selectedClient) : undefined;
  // Every client name with a config. Null until the list is in: a group run waits for it.
  const knownClients = useMemo(
    () => (!loading && clients.length > 0 ? new Set(clients.map((c) => c.name)) : null),
    [clients, loading]
  );
  // A group run leaves out a name that is not a client, a client marked do not deploy and an
  // inactive client. There is no override inside a group. The start reads the same again.
  const groupSplit = useMemo(
    () => splitGroupClients(selectedGroup?.clients ?? [], flags, knownClients),
    [selectedGroup, flags, knownClients]
  );
  const skipByName = useMemo(() => new Map(groupSplit.skipped.map((s) => [s.clientName, s])), [groupSplit]);
  // A Dev image on a customer's client must be confirmed by typing the client's name.
  const devToCustomerClient =
    channel === 'numa-deploy-dev' && Boolean(selectedClientObj) && !selectedClientObj?.config.devInstance;
  const groupHasCustomerClients = useMemo(() => {
    if (!selectedGroup) return false;
    return selectedGroup.clients.some((name) => {
      const client = clients.find((c) => c.name === name);
      return client ? !client.config.devInstance : false;
    });
  }, [selectedGroup, clients]);
  const devToCustomerGroup = channel === 'numa-deploy-dev' && groupHasCustomerClients;

  const busy = submitting !== null;
  const canStartSingle = Boolean(
    selectedClient && selectedTag && !busy && sfnArn && !clientBusy && label.trim().length > 0
  );
  const canStartGroup = Boolean(
    selectedGroup && selectedTag && !busy && !groupConfigMissing && selectedGroup.clients.length > 0 && knownClients
  );

  // What is still missing, said in words beside the buttons, so a greyed-out button is never a puzzle.
  let waitingFor = '';
  if (!busy && sfnArn) {
    if (mode === 'single') {
      const missing: string[] = [];
      if (!selectedClient) missing.push('pick a client');
      if (!selectedTag) missing.push('pick an image');
      if (!label.trim()) missing.push('add a label');
      if (missing.length > 0) {
        const text =
          missing.length > 1 ? `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}` : missing[0];
        waitingFor = `To start, ${text}.`;
      }
    } else if (!groupConfigMissing) {
      if (!selectedGroup) waitingFor = groupsLoaded ? 'To start, make a group.' : '';
      else if (!selectedTag) waitingFor = 'To start, pick an image.';
      else if (!knownClients && !loading)
        waitingFor = 'The client list did not load, so a group run cannot start. Reload the page.';
    }
  }

  const startSingle = async (kind: 'deploy' | 'plan') => {
    if (!user?.email) {
      setError('Your session has ended. Sign in again, then start the deployment.');
      return;
    }
    setSubmitting(kind);
    setError(null);
    setConfirmError(null);
    try {
      const { deploymentId } = await startDeployment({
        clientName: selectedClient,
        imageTag: selectedTag,
        repository: channel,
        initiatedBy: user.email,
        deploymentLabel: label.trim() || undefined,
        mode: kind,
        // The confirm step shows the block and takes the client's name before this runs. The
        // start reads the block again, and refuses if it is not the one the person was shown.
        ...(kind === 'deploy' && selectedBlock ? { acknowledgedBlockSetAt: selectedBlock.setAt } : {}),
      });
      // The record appears a moment after the start. Wait for it so the page can show the run at once.
      await waitForDeploymentRecord(deploymentId);
      setConfirming(null);
      // The label is cleared after a deploy. A plan keeps it, because the deploy that follows a plan uses the same one.
      if (kind === 'deploy') setLabel('');
      onStarted({ kind, deploymentId, clientName: selectedClient });
    } catch (e) {
      if (e instanceof DeployBlockedError) {
        // Somebody blocked the client, or changed the block, after this card read the tags.
        // Nothing started. Show the block as it stands and ask again.
        setConfirmError(e.message);
        setConfirming('single');
        void flags.reload();
        return;
      }
      console.error('Failed to start deployment:', e);
      setConfirming(null);
      setError(e instanceof Error ? e.message : 'Could not start the deployment. Try again.');
    } finally {
      setSubmitting(null);
    }
  };

  const startGroup = async () => {
    if (!user?.email || !selectedGroup) {
      setError('Your session has ended. Sign in again, then start the deployment.');
      return;
    }
    setSubmitting('group');
    setError(null);
    setConfirmError(null);
    try {
      const { groupRunId, skipped, skippedSaved } = await startGroupDeployment({
        groupName: selectedGroup.groupName,
        // The whole group goes in. The start takes out the clients marked do not deploy, and
        // refuses when they are not the ones the confirm step listed.
        clients: selectedGroup.clients,
        shownSkipped: groupSplit.skipped.map((s) => s.clientName),
        imageTag: selectedTag,
        repository: channel,
        initiatedBy: user.email,
        deploymentLabel: groupLabel.trim() || selectedGroup.groupName,
        maxConcurrency: clamp(concurrency),
      });
      await waitForDeploymentRecord(groupRunId);
      setConfirming(null);
      setGroupLabel('');
      onStarted({ kind: 'group', groupRunId });
      if (!skippedSaved) {
        setError(
          `The group run started. The list of clients it skipped was not saved, so the group page does not show it: ${skipped.map((s) => s.clientName).join(', ')}.`
        );
      }
    } catch (e) {
      if (e instanceof BlockedClientsChangedError) {
        // The tags were read again for the start, and this card now shows the new list. The
        // message names what the start found, in case the client list changed as well.
        const now = describeSkips(e.skipped);
        setConfirmError(now ? `${e.message} ${now}` : `${e.message} It now skips no client.`);
        return;
      }
      console.error('Failed to start group deployment:', e);
      setConfirming(null);
      setError(e instanceof Error ? e.message : 'Could not start the group deployment. Try again.');
    } finally {
      setSubmitting(null);
    }
  };

  const imageField = (
    <Form.Group>
      <Form.Label htmlFor="deploy-image">Image</Form.Label>
      <Form.Select
        id="deploy-image"
        value={selectedTag}
        onChange={(e) => {
          wantedTag.current = null;
          setSelectedTag(e.target.value);
        }}
        disabled={loading || busy || sortedImages.length === 0}
      >
        {sortedImages.length === 0 && (
          <option value="">{loading ? 'Loading images' : 'No images in this channel'}</option>
        )}
        {sortedImages.map((img, index) => (
          <option key={img.digest + img.tag} value={img.tag}>
            {imageOptionLabel(img, index === 0)}
          </option>
        ))}
      </Form.Select>
      {channel === 'numa-deploy-dev' ? (
        <Form.Text className="deploy-start__warn">
          Dev images are built by hand from a branch. They did not go through the release flow.
        </Form.Text>
      ) : (
        selectedImage?.description && <Form.Text>{selectedImage.description}</Form.Text>
      )}
    </Form.Group>
  );

  const imageFacts = (
    <>
      <dt>Channel</dt>
      <dd>
        <Badge bg={channel === 'numa-deploy-dev' ? 'warning' : 'secondary'}>
          {CHANNELS.find((c) => c.id === channel)?.label}
        </Badge>
      </dd>
      <dt>Image</dt>
      <dd>
        <span className={selectedImage?.customName ? undefined : 'mono'} title={selectedTag}>
          {selectedImage?.customName || selectedTag}
        </span>
      </dd>
    </>
  );

  return (
    <>
      <Card className="deploy-start" ref={card}>
        <div className="deploy-start__head">
          <h2 className="deploy-start__title">Start a deployment</h2>
          <div className="deploy-start__switches">
            <div className="deploy-start__switch">
              <span className="deploy-start__switch-label">Image</span>
              <Nav
                variant="pills"
                className="nav-pills-custom"
                activeKey={channel}
                onSelect={(key) => key && setChannel(key as Channel)}
              >
                {CHANNELS.map((c) => (
                  <Nav.Item key={c.id}>
                    <Nav.Link eventKey={c.id} disabled={busy}>
                      {c.label}
                      <span className="deploy-start__count">{imagesByChannel[c.id].length}</span>
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </div>
            <div className="deploy-start__switch">
              <span className="deploy-start__switch-label">Deploy to</span>
              <Nav
                variant="pills"
                className="nav-pills-custom"
                activeKey={mode}
                onSelect={(key) => key && setMode(key as 'single' | 'group')}
              >
                <Nav.Item>
                  <Nav.Link eventKey="single" disabled={busy}>
                    One client
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="group" disabled={busy}>
                    A group
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
          </div>
        </div>

        {(!sfnArn || error) && (
          <div className="deploy-start__alerts">
            {!sfnArn && (
              <Alert variant="warning" className="mb-0">
                Deployments are not set up for this portal. The portal config has no deploy pipeline.
              </Alert>
            )}
            {error && (
              <Alert variant="danger" className="mb-0" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </div>
        )}

        {mode === 'single' ? (
          <div className="deploy-start__body">
            <div>
              <Form.Label>Client</Form.Label>
              <ClientSelectGroup
                value={selectedClient}
                onChange={setSelectedClient}
                clients={clients}
                disabled={loading || busy}
                loading={loading}
              />
            </div>
            <div className="deploy-start__fields">
              {imageField}
              <Form.Group>
                <Form.Label htmlFor="deploy-label">Deployment label</Form.Label>
                <Form.Control
                  id="deploy-label"
                  type="text"
                  placeholder="Stable release 2.94"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  disabled={loading || busy}
                />
                <Form.Text>Required. A short name for this run, shown in the history.</Form.Text>
              </Form.Group>
              {clientBusy && (
                <Alert variant="warning" className="mb-0">
                  A deployment is already running for {selectedClient}. Start this one when it ends.
                </Alert>
              )}
              {selectedBlock && <DeployBlockNotice clientName={selectedClient} block={selectedBlock} />}
              <div className="deploy-start__actions">
                <Button
                  variant="primary"
                  onClick={() => {
                    setConfirmError(null);
                    setConfirming('single');
                  }}
                  disabled={!canStartSingle}
                >
                  Start deployment
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => void startSingle('plan')}
                  disabled={!canStartSingle}
                  title="Work out what the deploy would change, and change nothing."
                >
                  {submitting === 'plan' && <Spinner size="sm" className="me-2" />}
                  Plan only
                </Button>
                {waitingFor && <span className="deploy-start__hint">{waitingFor}</span>}
              </div>
            </div>
          </div>
        ) : (
          <div className="deploy-start__body">
            <div>
              {groupConfigMissing && (
                <Alert variant="info" className="mb-0">
                  Group deployments are not set up for this portal.
                </Alert>
              )}
              {!groupConfigMissing && loadingGroups && !groupsLoaded && (
                <p className="deploy-start__note">
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Loading groups
                </p>
              )}
              {!groupConfigMissing && groupsLoaded && groups.length === 0 && (
                <Alert variant="secondary">
                  No deployment groups yet. Make one to deploy to several clients at once.
                </Alert>
              )}
              {groups.length > 0 && (
                <Form.Group className="mb-2">
                  <Form.Label htmlFor="deploy-group">Group</Form.Label>
                  <Form.Select
                    id="deploy-group"
                    value={selectedGroupName}
                    onChange={(e) => setSelectedGroupName(e.target.value)}
                    disabled={busy || loadingGroups}
                  >
                    {groups.map((g) => (
                      <option key={g.groupName} value={g.groupName}>
                        {`${g.groupName} (${g.clients.length})${g.managed ? ' · automatic' : ''}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
              {!groupConfigMissing && (
                <div className="deploy-start__group-actions">
                  {selectedGroup && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setEditor({ open: true, group: selectedGroup })}
                      disabled={busy || loadingGroups || Boolean(selectedGroup.managed)}
                      title={
                        selectedGroup.managed
                          ? 'This group is kept up to date by itself and cannot be edited.'
                          : undefined
                      }
                    >
                      Edit group
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setEditor({ open: true, group: null })}
                    disabled={busy}
                  >
                    New group
                  </Button>
                </div>
              )}
              {selectedGroup && (
                <>
                  <Form.Label>Clients ({selectedGroup.clients.length})</Form.Label>
                  <div className="deploy-chips deploy-start__clients">
                    {selectedGroup.clients.map((client) => {
                      const skip = skipByName.get(client);
                      return (
                        <Badge
                          bg={!skip ? 'secondary' : skipKind(skip) === 'deploy-block' ? 'danger' : 'warning'}
                          key={client}
                          title={skip ? `Skipped. ${skipLine(skip)}` : undefined}
                        >
                          {client}
                        </Badge>
                      );
                    })}
                  </div>
                  {groupSplit.skipped.length > 0 && (
                    <p className="deploy-start__skipped mt-2 mb-0">{describeSkips(groupSplit.skipped)}</p>
                  )}
                  {selectedGroup.managed ? (
                    <p className="deploy-start__note mt-2 mb-0">
                      This group keeps itself up to date. It holds every{' '}
                      {selectedGroup.inactiveLeftOut ? 'active customer stack' : 'customer stack'}, plus{' '}
                      <span className="mono">arcanum-prod-trial</span>,{' '}
                      <span className="mono">arcanum-prod-numa-demo</span> and{' '}
                      <span className="mono">arcanum-council-trial</span>.{' '}
                      {selectedGroup.inactiveLeftOut
                        ? selectedGroup.inactiveLeftOut.length > 0 &&
                          `${selectedGroup.inactiveLeftOut.length} inactive ${selectedGroup.inactiveLeftOut.length === 1 ? 'client is' : 'clients are'} left out.`
                        : 'The inactive tags could not be read, so inactive clients are in this list too.'}
                    </p>
                  ) : (
                    <p className="deploy-start__note mt-2 mb-0">
                      A group run skips each client marked inactive or do not deploy, and any name that is not a client.
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="deploy-start__fields">
              {imageField}
              <div className="deploy-start__pair">
                <Form.Group>
                  <Form.Label htmlFor="deploy-concurrency">Clients deployed at once</Form.Label>
                  <InputGroup>
                    <Form.Control
                      id="deploy-concurrency"
                      type="number"
                      min={1}
                      max={maxConcurrency}
                      value={concurrency}
                      onChange={(e) => setConcurrency(clamp(Number(e.target.value) || 1))}
                      disabled={busy || !selectedGroup}
                    />
                    <InputGroup.Text>clients</InputGroup.Text>
                  </InputGroup>
                  <Form.Text>
                    Up to {maxConcurrency} at once.{' '}
                    {selectedGroup?.maxConcurrency
                      ? `This group's own setting is ${selectedGroup.maxConcurrency}.`
                      : `The portal default is ${defaultConcurrency}.`}
                  </Form.Text>
                </Form.Group>
                <Form.Group>
                  <Form.Label htmlFor="deploy-group-label">Deployment label</Form.Label>
                  <Form.Control
                    id="deploy-group-label"
                    type="text"
                    placeholder={selectedGroup?.groupName || 'Label for this group run'}
                    value={groupLabel}
                    onChange={(e) => setGroupLabel(e.target.value)}
                    disabled={busy || !selectedGroup}
                  />
                  <Form.Text>Leave empty to use the group's name.</Form.Text>
                </Form.Group>
              </div>
              <div className="deploy-start__actions">
                <Button
                  variant="primary"
                  onClick={() => {
                    setConfirmError(null);
                    setConfirming('group');
                  }}
                  disabled={!canStartGroup}
                >
                  Start deployment
                </Button>
                {waitingFor && <span className="deploy-start__hint">{waitingFor}</span>}
              </div>
            </div>
          </div>
        )}
      </Card>

      <ConfirmDialog
        show={confirming === 'single'}
        title={selectedBlock ? 'Deploy to a blocked client?' : 'Start this deployment?'}
        confirmLabel={selectedBlock ? 'Deploy anyway' : 'Start deployment'}
        variant={selectedBlock ? 'danger' : 'primary'}
        // One typed name covers both checks: a blocked client, and a Dev image on a customer stack.
        typeToConfirm={devToCustomerClient || selectedBlock ? selectedClient : undefined}
        busy={submitting === 'deploy'}
        error={confirmError}
        onCancel={() => setConfirming(null)}
        onConfirm={() => void startSingle('deploy')}
      >
        <dl className="deploy-confirm__facts">
          <dt>Client</dt>
          <dd>{selectedClient}</dd>
          {imageFacts}
          <dt>Label</dt>
          <dd>{label}</dd>
        </dl>
        {selectedBlock && (
          <>
            <DeployBlockNotice clientName={selectedClient} block={selectedBlock} className="mt-3" />
            <p className="deploy-confirm__hint">
              Typing the name starts the deploy in spite of the block. The block stays on, and the activity log records
              that you went ahead.
            </p>
          </>
        )}
        {devToCustomerClient && (
          <Alert variant="danger" className="mt-3 mb-0">
            You are about to put a Dev image on a customer stack. Dev images are built by hand from a branch and did not
            go through the release flow.
          </Alert>
        )}
        <p className="deploy-confirm__hint">A deploy takes between 15 and 50 minutes.</p>
      </ConfirmDialog>

      <ConfirmDialog
        show={confirming === 'group'}
        title="Start this group deployment?"
        confirmLabel={`Deploy to ${groupSplit.allowed.length} ${groupSplit.allowed.length === 1 ? 'client' : 'clients'}`}
        typeToConfirm={devToCustomerGroup ? selectedGroup?.groupName : undefined}
        busy={submitting === 'group'}
        error={confirmError}
        blocked={
          selectedGroup && groupSplit.allowed.length === 0
            ? 'Every client in this group is skipped, so there is nothing to start.'
            : undefined
        }
        onCancel={() => setConfirming(null)}
        onConfirm={() => void startGroup()}
      >
        <dl className="deploy-confirm__facts">
          <dt>Group</dt>
          <dd>{selectedGroup?.groupName}</dd>
          <dt>Clients</dt>
          <dd>
            {groupSplit.allowed.length}, up to {clamp(concurrency)} at once
          </dd>
          {imageFacts}
          <dt>Label</dt>
          <dd>{groupLabel.trim() || selectedGroup?.groupName}</dd>
        </dl>
        <SkippedClientsNotice skipped={groupSplit.skipped} className="mt-3 mb-0" />
        {devToCustomerGroup && (
          <Alert variant="danger" className="mt-3 mb-0">
            This group holds customer stacks, and you picked a Dev image. Dev images are built by hand from a branch and
            did not go through the release flow.
          </Alert>
        )}
      </ConfirmDialog>

      <GroupEditorModal
        show={editor.open}
        group={editor.group}
        groups={groups}
        clients={clients}
        defaultConcurrency={defaultConcurrency}
        maxConcurrency={maxConcurrency}
        onClose={() => setEditor((prev) => ({ ...prev, open: false }))}
        onSaved={(name) => {
          setEditor((prev) => ({ ...prev, open: false }));
          setSelectedGroupName(name);
          void loadGroups();
        }}
        onDeleted={() => {
          setEditor((prev) => ({ ...prev, open: false }));
          void loadGroups();
        }}
      />
    </>
  );
}

export default StartCard;
